import { EXPRESSION_TYPES } from "./expressionTypes";
import { RESULT_STATUS } from "./evaluators/result";
import { BooleanEvaluator } from "./evaluators/boolean";
import { ChoiceEvaluator } from "./evaluators/choice";
import { NumericEvaluator } from "./evaluators/numeric";
import { RelationalEvaluator } from "./evaluators/relational";
import { MultiInstanceEvaluator } from "./evaluators/multiInstance";
import { StringEvaluator } from "./evaluators/string";
import { DateEvaluator } from "./evaluators/date";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
import { ClientDate } from "Util/clientDate";
import { ATTACHMENT_APPROVAL_STATUS } from "Util/clientAttachment";
import { ENVELOPE_STATUS } from "ESignatures/eSignatureConstants";
import { PROGRAM_TYPES, PROGRAM_NAMESPACE } from "SharedStores/Program/programStore";
import { PROJECT_TYPES, PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { isModuleRegistered } from "SharedStores/util";

export class FormulaEvaluator {
    constructor($app, formula, storeContext, $serviceControllers) {
        this.formula = formula;
        this.boolean = new BooleanEvaluator(this, storeContext, $serviceControllers);
        this.choice = new ChoiceEvaluator(this, storeContext, $serviceControllers);
        this.numeric = new NumericEvaluator(this, storeContext, $serviceControllers);
        this.relational = new RelationalEvaluator(this, storeContext, $serviceControllers);
        this.string = new StringEvaluator(this, storeContext, $serviceControllers);
        this.date = new DateEvaluator(this, storeContext, $serviceControllers);
        this.multiInstanceEvaluator = new MultiInstanceEvaluator(this, storeContext, $serviceControllers);
        this.storeContext = storeContext;
        this.$serviceControllers = $serviceControllers;
        this.$app = $app;
    }
    /**
     * Gets an object that has a key value pair for all referenced values.
     * useful to make sure that watchers register dependencies in a sync fashion without having to
     * do a full eval (which is now async)
     */
    getReferencedValuesDictionary() {
        const state = this.storeContext.rootState;
        const getters = this.storeContext.rootGetters;
        if (!isModuleRegistered(PROGRAM_NAMESPACE, state)) {
            throw Error("program store not registered");
        }
        if (!isModuleRegistered(PROJECT_NAMESPACE, state)) {
            throw Error("project store not registered");
        }
        const map = {};

        const dataFieldPublicIds = this.getReferencedDataFieldIds();
        const attachmentPublicIds = this.getReferencedAttachmentIds();
        const envelopePublicIds = this.getReferencedEnvelopeIds();

        dataFieldPublicIds.forEach((publicId) => {
            const dataFieldDefinition = getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](publicId);
            if (dataFieldDefinition.IsMultiInstance) {
                const multiValue = getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId](publicId);
                map[publicId] = multiValue;
            } else {
                const value = getters[PROJECT_TYPES.getters.dataFieldValueFromId](publicId);
                map[publicId] = value;
            }
        });

        attachmentPublicIds.forEach((publicId) => {
            const attachment = getters[PROJECT_TYPES.getters.attachmentFromId](publicId);
            map[publicId] = attachment;
        });
        envelopePublicIds.forEach((publicId) => {
            const envelope = getters[PROJECT_TYPES.getters.getEnvelopeFromId](publicId);
            map[publicId] = envelope;
        });

        return map;
    }

    async eval() {
        const rootExpression = this.formula.RootExpression;
        switch (this.formula.ResultType) {
            case DATA_FIELD_TYPE.Decimal:
                this.formula.result = await this.evalNum(rootExpression);
                break;
            case DATA_FIELD_TYPE.Boolean:
                this.formula.result = await this.evalBool(rootExpression);
                break;
            case DATA_FIELD_TYPE.String:
                this.formula.result = await this.evalString(rootExpression);
                break;
            case DATA_FIELD_TYPE.DateTime:
                this.formula.result = await this.evalDate(rootExpression);
                break;
            default:
                console.error(
                    `formulaResultElement: Unable to evaluate result for unknown type ${this.formula.ResultType}`
                );
        }
    }
    async evalNum(exp) {
        let result = null;
        switch (exp.Type) {
            case EXPRESSION_TYPES.Addition:
                result = await this.numeric.add(exp);
                break;
            case EXPRESSION_TYPES.Subtraction:
                result = await this.numeric.subtract(exp);
                break;
            case EXPRESSION_TYPES.Multiplication:
                result = await this.numeric.multiply(exp);
                break;
            case EXPRESSION_TYPES.Division:
                result = await this.numeric.divide(exp);
                break;
            case EXPRESSION_TYPES.Constant:
                result = this.numeric.evaluateConstant(exp);
                break;
            case EXPRESSION_TYPES.DataFieldReference:
                result = await this.numeric.evaluateDataReference(exp);
                break;
            case EXPRESSION_TYPES.ConditionalNumeric:
                result = await this.numeric.evaluateConditionalNumeric(exp);
                break;
            case EXPRESSION_TYPES.Min:
                result = await this.numeric.min(exp);
                break;
            case EXPRESSION_TYPES.Max:
                result = await this.numeric.max(exp);
                break;
            case EXPRESSION_TYPES.Exponent:
                result = await this.numeric.exponent(exp);
                break;
            case EXPRESSION_TYPES.Logarithm:
                result = await this.numeric.logarithm(exp);
                break;
            case EXPRESSION_TYPES.StringLength:
                result = await this.string.stringLength(exp);
                break;
            case EXPRESSION_TYPES.CoalesceMultiInstanceValue:
                result = await this.multiInstanceEvaluator.evaluateCoalesceMultiInstanceValue(
                    this.$app,
                    exp,
                    DATA_FIELD_TYPE.Decimal
                );
                break;
            case EXPRESSION_TYPES.SumMultiInstanceValue:
                result = this.multiInstanceEvaluator.evaluateSumMultiInstanceValue(exp);
                break;
            case EXPRESSION_TYPES.CountTrueMultiInstanceValue:
                result = this.multiInstanceEvaluator.evaluateCountTrueMultiInstanceValue(exp);
                break;
            case EXPRESSION_TYPES.CountMatchingTextMultiInstanceValue:
                result = await this.multiInstanceEvaluator.evaluateCountMatchingTextMultiInstanceValue(exp);
                break;
            case EXPRESSION_TYPES.Round:
                result = await this.numeric.round(exp);
                break;
            case EXPRESSION_TYPES.DateDifference:
                result = await this.numeric.dateDifference(exp);
                break;
            default:
                result = null;
        }

        if (result && result.Status === RESULT_STATUS.Success) {
            result = checkNumericDataLimits(result);
            if (result.Status === RESULT_STATUS.Error) {
                exp.inError = true;
            }
        }

        exp.evalResult = result;
        return result;
    }
    async evalBool(exp) {
        let result = null;
        switch (exp.Type) {
            case EXPRESSION_TYPES.EqualTo:
                result = await this.relational.equalTo(exp);
                break;
            case EXPRESSION_TYPES.NotEqualTo:
                result = await this.relational.notEqualTo(exp);
                break;
            case EXPRESSION_TYPES.GreaterThan:
                result = await this.relational.greaterThan(exp);
                break;
            case EXPRESSION_TYPES.LessThan:
                result = await this.relational.lessThan(exp);
                break;
            case EXPRESSION_TYPES.GreaterThanOrEqualTo:
                result = await this.relational.greaterThanOrEqualTo(exp);
                break;
            case EXPRESSION_TYPES.LessThanOrEqualTo:
                result = await this.relational.lessThanOrEqualTo(exp);
                break;
            case EXPRESSION_TYPES.And:
                result = await this.boolean.and(exp);
                break;
            case EXPRESSION_TYPES.Or:
                result = await this.boolean.or(exp);
                break;
            case EXPRESSION_TYPES.Not:
                result = await this.boolean.not(exp);
                break;
            case EXPRESSION_TYPES.BooleanConstant:
                result = this.boolean.evaluateBooleanConstant(exp);
                break;
            case EXPRESSION_TYPES.BooleanDataFieldReference:
                result = this.boolean.evaluateBooleanDataReference(exp);
                break;
            case EXPRESSION_TYPES.AttachmentIsApprovedReference:
                result = this.boolean.evaluateAttachmentStatus(exp, ATTACHMENT_APPROVAL_STATUS.approved);
                break;
            case EXPRESSION_TYPES.AttachmentIsRejectedReference:
                result = this.boolean.evaluateAttachmentStatus(exp, ATTACHMENT_APPROVAL_STATUS.rejected);
                break;
            case EXPRESSION_TYPES.StringEqual:
                result = await this.string.stringEqualTo(exp);
                break;
            case EXPRESSION_TYPES.StringNotEqual:
                result = await this.string.stringNotEqualTo(exp);
                break;
            case EXPRESSION_TYPES.ChoiceEqual:
                result = this.choice.choiceEqualTo(exp);
                break;
            case EXPRESSION_TYPES.ChoiceNotEqual:
                result = this.choice.choiceNotEqualTo(exp);
                break;
            case EXPRESSION_TYPES.DateRelational:
                result = await this.date.evaluateDateRelational(exp);
                break;
            case EXPRESSION_TYPES.ExistsSameMultiInstanceValue:
                result = this.multiInstanceEvaluator.evaluateExistsSameMultiInstanceValue(exp);
                break;
            case EXPRESSION_TYPES.CoalesceMultiInstanceValue:
                result = await this.multiInstanceEvaluator.evaluateCoalesceMultiInstanceValue(
                    this.$app,
                    exp,
                    DATA_FIELD_TYPE.Boolean
                );
                break;
            case EXPRESSION_TYPES.IsEmpty:
                result = this.boolean.evaluateIsEmpty(exp);
                break;
            case EXPRESSION_TYPES.EnvelopeCompleted:
                result = await this.boolean.evaluateEnvelopeStatus(exp, ENVELOPE_STATUS.Completed);
                break;
            default:
                result = null;
        }

        exp.evalResult = result;
        return result;
    }
    async evalDate(exp) {
        let result = null;
        switch (exp.Type) {
            case EXPRESSION_TYPES.DateConstant:
                result = this.date.evaluateDateConstant(exp);
                break;
            case EXPRESSION_TYPES.DateDataFieldReference:
                result = this.date.evaluateDateDataReference(this.$app, exp);
                break;
            case EXPRESSION_TYPES.DateArithmetic:
                result = await this.date.evaluateDateArithmetic(exp);
                break;
            case EXPRESSION_TYPES.CoalesceMultiInstanceValue:
                result = await this.multiInstanceEvaluator.evaluateCoalesceMultiInstanceValue(
                    this.$app,
                    exp,
                    DATA_FIELD_TYPE.DateTime
                );
                break;
            case EXPRESSION_TYPES.ConditionalDate:
                result = await this.date.evaluateConditionalDate(exp);
                break;
            default:
                result = null;
        }
        if (result.Value && !(result.Value instanceof ClientDate)) {
            result.Value = new ClientDate(this.$app, result.Value.Year, result.Value.Month, result.Value.Day);
        }

        exp.evalResult = result;
        return result;
    }
    async evalString(exp) {
        let result = null;
        switch (exp.Type) {
            case EXPRESSION_TYPES.StringConstant:
                result = this.string.evaluateStringConstant(exp);
                break;
            case EXPRESSION_TYPES.StringDataFieldReference:
                result = this.string.evaluateStringDataReference(exp);
                break;
            case EXPRESSION_TYPES.ConditionalString:
                result = await this.string.evaluateConditionalString(exp);
                break;
            case EXPRESSION_TYPES.ConcatenateStrings:
                result = await this.string.evaluateConcatenateStrings(exp);
                break;
            case EXPRESSION_TYPES.ProjectNumberReference:
                result = this.string.evaluateProjectNumberReference(exp);
                break;
            case EXPRESSION_TYPES.DataFieldReferenceAsString:
                result = this.string.evaluateDataFieldReferenceAsString(exp);
                break;
            case EXPRESSION_TYPES.CoalesceMultiInstanceValue:
                result = await this.multiInstanceEvaluator.evaluateCoalesceMultiInstanceValue(
                    this.$app,
                    exp,
                    DATA_FIELD_TYPE.String
                );
                break;
            default:
                result = null;
        }

        exp.evalResult = result;
        return result;
    }
    verifyTreeAndGetAsList() {
        const allExpressions = [];
        this.all(this.formula.RootExpression, new Set(), allExpressions);
        return allExpressions;
    }
    /**
     * @param {Object} exp
     * @param {Set} visited
     * @param {Array} expressions
     */
    all(exp, visited, expressions) {
        if (visited.has(exp.Id)) {
            throw new Error("Loop found in formula expression");
        }
        visited.add(exp.Id);
        expressions.push(exp);
        const children = exp.Children || [];
        children.forEach((child) => {
            this.all(child, visited, expressions);
        });
    }
    expressionTypeReferencesProjectData(type) {
        return (
            type === EXPRESSION_TYPES.DataFieldReference ||
            type === EXPRESSION_TYPES.BooleanDataFieldReference ||
            type === EXPRESSION_TYPES.StringDataFieldReference ||
            type === EXPRESSION_TYPES.DateDataFieldReference ||
            type === EXPRESSION_TYPES.ChoiceEqual ||
            type === EXPRESSION_TYPES.ChoiceNotEqual ||
            type === EXPRESSION_TYPES.DataFieldReferenceAsString ||
            type === EXPRESSION_TYPES.ExistsSameMultiInstanceValue ||
            type === EXPRESSION_TYPES.CoalesceMultiInstanceValue ||
            type === EXPRESSION_TYPES.SumMultiInstanceValue ||
            type === EXPRESSION_TYPES.CountTrueMultiInstanceValue ||
            type === EXPRESSION_TYPES.CountMatchingTextMultiInstanceValue ||
            type === EXPRESSION_TYPES.IsEmpty
        );
    }

    expressionTypeReferencesAttachment(type) {
        return (
            type === EXPRESSION_TYPES.IsEmpty ||
            type === EXPRESSION_TYPES.AttachmentIsApprovedReference ||
            type === EXPRESSION_TYPES.AttachmentIsRejectedReference
        );
    }

    expressionTypeReferencesEnvelopeDefinition(type) {
        return type === EXPRESSION_TYPES.EnvelopeCompleted;
    }

    getReferencedDataFieldIds() {
        const publicIds = this.verifyTreeAndGetAsList()
            .filter((e) => this.expressionTypeReferencesProjectData(e.Type) && e.PublicDataFieldDefinitionId)
            .map((e) => e.PublicDataFieldDefinitionId);
        return publicIds;
    }

    getReferencedAttachmentIds() {
        const expressions = this.verifyTreeAndGetAsList();
        const publicIds = expressions
            .filter((e) => this.expressionTypeReferencesAttachment(e.Type) && e.PublicAttachmentDefinitionId)
            .map((e) => e.PublicAttachmentDefinitionId);
        return publicIds;
    }

    getReferencedEnvelopeIds() {
        const expressions = this.verifyTreeAndGetAsList();
        const publicIds = expressions
            .filter((e) => this.expressionTypeReferencesEnvelopeDefinition(e.Type) && e.PublicEnvelopeDefinitionId)
            .map((e) => e.PublicEnvelopeDefinitionId);
        return publicIds;
    }
}

//value in C# is 79228162514264337593543950335, but we don't have enough precision in javascript
const DECIMAL_MAX_VALUE = 7.922816251426434e28;

//value in C# is -79228162514264337593543950335, but we don't have enough precision in javascript
const DECIMAL_MIN_VALUE = -7.922816251426434e28;

// Javascript numbers and C# doubles have a max of roughly 1e308,
// but the max for C# decimals is much lower, roughly 7.9e28.
// If any subexpression is greater than that, evaluation will fail server-side,
// so block it here.
function checkNumericDataLimits(result) {
    if (result != null && result.Value > DECIMAL_MAX_VALUE) {
        const errorMessage = `Result ${result.Value} is greater than the maximum allowed decimal value`;
        return { Status: RESULT_STATUS.Error, Message: errorMessage };
    } else if (result != null && result.Value < DECIMAL_MIN_VALUE) {
        const errorMessage = `Result ${result.Value} is less than the minimum allowed decimal value`;
        return { Status: RESULT_STATUS.Error, Message: errorMessage };
    }
    return result;
}
